/**
 * Error carrying the parsed API error body — `detail` (field-level
 * explanation, e.g. zod issues) is surfaced in the message so operator-facing
 * pages can show WHY a request was rejected, not just the error code.
 */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly data: {
      error?: string;
      detail?: string;
      conflictingProduct?: { id: number; sku: string | null; name: string };
      gtin?: string;
      [key: string]: unknown;
    },
  ) {
    super(data?.detail ?? data?.error ?? `HTTP ${status}`);
    this.name = 'ApiError';
  }
}

export async function fetchJson<T>(path: string, init?: RequestInit): Promise<T> {
  const hasBody = init?.body !== undefined && init.body !== null;
  const headers = new Headers(init?.headers);
  if (!headers.has('accept')) {
    headers.set('accept', 'application/json');
  }
  if (hasBody && !headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }
  const response = await fetch(path, {
    credentials: 'include',
    ...init,
    headers,
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as {
      error?: string;
      detail?: string;
      conflictingProduct?: { id: number; sku: string | null; name: string };
      gtin?: string;
    };
    throw new ApiError(response.status, body);
  }
  return (await response.json()) as T;
}
